import { useEffect, useState } from 'react';
import { MapPin, Phone, EnvelopeSimple, Clock, PhoneCall } from '@phosphor-icons/react';
import FileUpload from './FileUpload';
import './ContactSection.css';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    btw_nummer: '',
    type: 'werkgever', // werkgever or werknemer
  });
  const [kvkFile, setKvkFile] = useState(null);
  const [certFiles, setCertFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // When a "Solliciteer Direct" button in the Vacatures section is clicked,
  // switch this form to the job-seeker tab and pre-fill the vacancy.
  useEffect(() => {
    const onApply = (e) => {
      const job = e.detail || {};
      setFormData((prev) => ({
        ...prev,
        type: 'werknemer',
        message: job.title
          ? `Ik wil graag solliciteren op: ${job.title}${job.location ? ` (${job.location})` : ''}.\n\n`
          : prev.message,
      }));
    };
    window.addEventListener('ozel:apply', onApply);
    return () => window.removeEventListener('ozel:apply', onApply);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isWerknemer = formData.type === 'werknemer';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setError('');

    // Job-seekers must supply KvK-uittreksel, certificering(en) and BTW-nummer.
    if (isWerknemer) {
      if (!formData.btw_nummer.trim()) return setError('Vul uw BTW-nummer in.');
      if (!kvkFile) return setError('Upload uw KvK-uittreksel.');
      if (certFiles.length === 0) return setError('Upload minimaal één certificering.');
    }

    setSending(true);
    try {
      const fd = new FormData();
      fd.append('type', formData.type);
      fd.append('name', formData.name);
      fd.append('company', formData.company);
      fd.append('email', formData.email);
      fd.append('phone', formData.phone);
      fd.append('message', formData.message);
      if (isWerknemer) {
        fd.append('btw_nummer', formData.btw_nummer);
        fd.append('kvk', kvkFile);
        certFiles.forEach((f) => fd.append('certificeringen', f));
      }

      const res = await fetch('/api/contact', { method: 'POST', body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Er ging iets mis. Probeer het later opnieuw.');
      }

      setSubmitted(true);
      setFormData({ name: '', company: '', email: '', phone: '', message: '', btw_nummer: '', type: 'werkgever' });
      setKvkFile(null);
      setCertFiles([]);
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-container">
        {/* Header */}
        <div className="contact-header">
          <span className="contact-label">Contact</span>
          <h2 className="contact-title">Neem Contact Met Ons Op</h2>
          <p className="contact-subtitle">
            Heeft u personeelsbehoeften of wilt u solliciteren? Wij staan voor u klaar.
          </p>
        </div>

        {/* Content */}
        <div className="contact-content">
          {/* Form */}
          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              {/* Type Toggle */}
              <div className="contact-type-toggle">
                <label className={`type-option ${formData.type === 'werkgever' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="type"
                    value="werkgever"
                    checked={formData.type === 'werkgever'}
                    onChange={handleChange}
                  />
                  <span>Ik zoek personeel</span>
                </label>
                <label className={`type-option ${formData.type === 'werknemer' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="type"
                    value="werknemer"
                    checked={formData.type === 'werknemer'}
                    onChange={handleChange}
                  />
                  <span>Ik zoek werk</span>
                </label>
              </div>

              {/* Fields */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Naam</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Uw naam"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company">Bedrijf</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={formData.type === 'werkgever' ? 'Uw bedrijf' : 'Optioneel'}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">E-mail</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="uw@email.nl"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Telefoon</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 12345678"
                  />
                </div>
              </div>

              {isWerknemer && (
                <div className="form-group">
                  <label htmlFor="btw_nummer">BTW-nummer <span className="req-star">*</span></label>
                  <input
                    type="text"
                    id="btw_nummer"
                    name="btw_nummer"
                    value={formData.btw_nummer}
                    onChange={handleChange}
                    required
                    placeholder="NL000000000B00"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="message">Bericht</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder={formData.type === 'werkgever' ? 'Beschrijf uw personeelsbehoefte...' : 'Vertel ons over uw ervaring en wat u zoekt...'}
                />
              </div>

              {isWerknemer && (
                <div className="contact-uploads">
                  <FileUpload
                    label="KvK-uittreksel"
                    required
                    files={kvkFile}
                    onChange={setKvkFile}
                  />
                  <FileUpload
                    label="Certificeringen"
                    multiple
                    required
                    files={certFiles}
                    onChange={setCertFiles}
                  />
                </div>
              )}

              {error && <p className="contact-error">{error}</p>}

              <button type="submit" className="contact-submit" disabled={sending || submitted}>
                {submitted ? 'Verzonden!' : sending ? 'Versturen...' : 'Verstuur Bericht'}
              </button>
            </form>
          </div>

          {/* Info */}
          <div className="contact-info-wrapper">
            <div className="contact-info-card">
              <h3>Contactgegevens</h3>
              
              <div className="contact-info-item">
                <MapPin size={18} weight="regular" className="contact-info-icon" aria-hidden="true" />
                <div className="contact-info-text">
                  <span className="info-label">Adres</span>
                  <span className="info-value">Schuttevaerweg 81<br />3044BA Rotterdam</span>
                </div>
              </div>

              <div className="contact-info-item">
                <Phone size={18} weight="regular" className="contact-info-icon" aria-hidden="true" />
                <div className="contact-info-text">
                  <span className="info-label">Telefoon</span>
                  <span className="info-value">010 415 9100</span>
                </div>
              </div>

              <div className="contact-info-item">
                <EnvelopeSimple size={18} weight="regular" className="contact-info-icon" aria-hidden="true" />
                <div className="contact-info-text">
                  <span className="info-label">E-mail</span>
                  <span className="info-value">info@ozel.eu</span>
                </div>
              </div>

              <div className="contact-info-item">
                <Clock size={18} weight="regular" className="contact-info-icon" aria-hidden="true" />
                <div className="contact-info-text">
                  <span className="info-label">Openingstijden</span>
                  <span className="info-value">Ma - Vr: 08:30 - 17:00</span>
                </div>
              </div>
            </div>

            {/* Quick CTA */}
            <div className="contact-quick-cta">
              <h4>Snel contact?</h4>
              <p>Bel ons direct voor dringende zaken</p>
              <a href="tel:+31104159100" className="quick-call-btn">
                <PhoneCall size={17} weight="regular" aria-hidden="true" />
                Bel Direct
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
